import CONFIG from "../temp/config";
import {
  APP_SECTION,
  STORAGE_KEYS,
  PRINCIPAL,
  PRODUCT_ID,
  LEAD_TYPE,
  CHANNEL_TYPE,
  APP_DATA,
  AGENT_INFO_V01, AGENT_INFO, RECOMMENDATION_CODE
} from "./constants";
import {isValid, mygaSession} from "./myga-session";
import {DEVICE,UAParser} from "ua-parser-js"
import moment from 'moment';
import {NassauLog} from "../scripts/myga-utils"
import {Analytics} from "./analytics";
import {getValidationReport} from "./validation";

const NSRE_ENDPOINT = Object.freeze({
  SaveAppData : 'saveappdata',
  StartApplication : 'startapplication',
  GetShoppingCart : 'getshoppingcart',
  SetSubmitApp : 'setforsubmission',
  DeletePendingApp : 'deletependingapp',
  GetGuestId : 'requestguestid', //'getguestid'
  GetReferralAgent : 'getreferralagent',
  IDScan : 'idscan',
  AgentInfo : 'agentinfo'
});

const SyncStatus = Object.freeze({
  StartApplicationRequested : 'StartApplicationRequested',
  StartApplicationReceived : 'StartApplicationReceived',
  StartApplicationSuccess : 'StartApplicationSuccess',
  StartApplicationError : 'StartApplicationError',
  SaveAppRequested : 'SaveAppRequested',
  SaveAppReceived : 'SaveAppReceived',
  SaveAppSuccess : 'SaveAppSuccess',
  SaveAppError : 'SaveAppError'
});

const ShoppingCartStatus = Object.freeze({
  ShoppingCartRequested : 'ShoppingCartRequested',
  ShoppingCartReceived : 'ShoppingCartReceived',
  ShoppingCartSuccess : 'ShoppingCartSuccess',
  ShoppingCartError : 'ShoppingCartError'
});

const SubmitAppStatus = Object.freeze({
  SubmitAppRequested : 'SubmitAppRequested',
  SubmitAppReceived : 'SubmitAppReceived',
  SubmitAppSuccess : 'SubmitAppSuccess',
  SubmitAppError : 'SubmitAppError'
});

const DeleteAppStatus = Object.freeze({
  DeleteAppRequested : 'DeleteAppRequested',
  DeleteAppReceived : 'DeleteAppReceived',
  DeleteAppSuccess : 'DeleteAppSuccess',
  DeleteAppError : 'DeleteAppError'
});

const GetGuestIdStatus = Object.freeze({
  GetGuestIdRequested : 'GetGuestIdRequested',
  GetGuestIdReceived : 'GetGuestIdReceived',
  GetGuestIdSuccess : 'GetGuestIdSuccess',
  GetGuestIdError : 'GetGuestIdError'
});

const GetReferralAgentStatus = Object.freeze({
  GetReferralAgentRequested : 'GetReferralAgentRequested',
  GetReferralAgentReceived : 'GetReferralAgentReceived',
  GetReferralAgentSuccess : 'GetReferralAgentSuccess',
  GetReferralAgentError : 'GetReferralAgentError'
});

/**
 * common json response handler
 * @param response
 * @returns {Promise<any>}
 */
let jsonResponseHandler = async (response) => {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  } else {
    let json = await response.json();
    // check the NSRE status flag
    if (json.status !== 'OK') {
      throw new Error(`NSRE error! status: ${json.status}`);
    } else {
      return json;
    }
  }
};


/**
 * Gets the user data for a guest, given the provided email.
 * @param email
 * @returns {Promise<*>}
 */
export let getGuestIdData = async (email = '') => {

  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.GetGuestId;
  
  // Parse the userAgent
  let uaParser = UAParser();
  
  // See if there is any device data
  let device = uaParser['device'];
  
  // Create the POST body for guest access
  let body = JSON.stringify({
    'vendorId': mygaSession.getGuestId(),
    'email': email,
    'packageName': 'nsre.reactjs',
    'deviceName': device[DEVICE.NAME] ? device[DEVICE.NAME] : 'none',
    'deviceModel': device[DEVICE.MODEL] ? device[DEVICE.MODEL] : 'none'
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json'
  };
  
  // NassauLog('uaParser: ' + JSON.stringify(uaParser));
  // NassauLog('device: ' + JSON.stringify(device));
  // NassauLog('body: ' + body);
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });

  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('getGuestIdData: ' + JSON.stringify(json));
  
  // validate the principal
  let principal = json.principal;
  if (!isValid(principal))  {
    throw new Error(`getGuestIdData error! principal not found`);
  }
  
  // save the principal to the myga-session
  return await mygaSession.setMygaSessionValue(STORAGE_KEYS.NsrePrincipalGuest, principal);
  
};

/**
 * Function to start the MYGA application process.
 *
 * Returns the initial application data and updates
 * the mygaSession currentApp field.
 *
 * returns a promise with the JSON response if successful
 *
 * @returns {Promise<*>}
 */
export let startApplication = async () => {
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`startApplication error! principal not found`);
  } else {
    NassauLog("startApplication principal: " + JSON.stringify(principal));
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`startApplication error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.StartApplication;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'startDate': moment().format('MM/DD/YYYY'),
    'productId': PRODUCT_ID.simpleAnnuity,
    'leadType': LEAD_TYPE.mygaLead,
    'channelSource': CHANNEL_TYPE.mobilewebsite
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('startApplication: ' + JSON.stringify(json));
  
  // validate the application
  let app = json.app;
  if (!isValid(app))  {
    throw new Error(`startApplication error! app data not found.`);
  }
  
  // 2021.05.17.ADivelbiss - trigger the GTM event for an application start
  Analytics.fireAppStarted();
  
  // save the principal to the myga-session
  return await mygaSession.setCurrentApp(app);
  
};

/**
 * Request to save the current application located in
 * mygaSession.getCurrentApp()
 *
 * returns a promise with the JSON response if successful
 *
 * @param section - from APP_SECTION constants
 * @returns {Promise<*>}
 */
export let saveApplication = async (section) => {
  
  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`saveApplication error! current appData not found`);
  }
  
  // Get the desired section from the app data.
  let sectionData = appData[APP_DATA.appData][section];
  if (!isValid(sectionData)) {
    // 2021.12.15.ADivelbiss - We may need to actually clear a section on the backend
    // so instead of throwing an exception, let's set the sectionData to an empty object.
    // The original motivation was to remove agent information that may have already been sent to the backend
    sectionData = {};
    
    // original code herel
    // throw new Error(`saveApplication error! section ${section} not found in appData`);
  }
  let sectionDataWrapper = {};
  sectionDataWrapper[section] = sectionData;
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`startApplication error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`startApplication error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.SaveAppData;
  
  // Form the POST body
  let body = JSON.stringify({
    'userId': appData[APP_DATA.userId],
    'applicationId': appData[APP_DATA.applicationId],
    'startDate': appData[APP_DATA.startDate],
    'formID': appData[APP_DATA.formID],
    'sectionName': section,
    'appData': sectionDataWrapper,
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('saveApplication: ' + JSON.stringify(json));
  
  // Return the result
  return json;
};

/**
 * Submits the application located in
 * mygaSession.getCurrentApp()
 *
 * returns a promise with the JSON response if successful
 * @returns {Promise<*>}
 */
export let submitApp = async () => {

  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`submitApp error! current appData not found`);
  }
  
  // Get the desired section from the app data.
  let submitData = appData[APP_DATA.appData][APP_SECTION.submission];
  if (!isValid(appData)) {
    throw new Error(`submitApp error! submission data not found in appData`);
  }
  let submitDataWrapper = {};
  submitDataWrapper[APP_SECTION.submission] = submitData;

  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`submitApp error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`submitApp error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.SetSubmitApp;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'userId': appData[APP_DATA.userId],
    'applicationId': appData[APP_DATA.applicationId],
    'appData': submitDataWrapper,
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('submitApp: ' + JSON.stringify(json));
  // update the submit status on success
  if (json.status) {
    mygaSession.setCurrentAppSubmitStatus(json.status);
  }
  
  // Return the result
  return json;
};

/**
 * Deletes the app currently located  in
 * mygaSession.getCurrentApp()
 *
 * returns a promise with the JSON response if successful
 * @returns {Promise<*>}
 */
export let deleteApp = async () => {
  
  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`deleteApp error! current appData not found`);
  }
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`deleteApp error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`deleteApp error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.DeletePendingApp;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'userId': appData[APP_DATA.userId],
    'applicationId': appData[APP_DATA.applicationId],
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('deleteApp: ' + JSON.stringify(json));
  
  // Clear the current app
  await mygaSession.setCurrentApp(null);
  
  // Return the result
  return json;
};

/**
 * Requests the
 * @returns {Promise<*>}
 */
export let getShoppingCart = async () => {
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`getShoppingCart error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`getShoppingCart error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.GetShoppingCart;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'productId': PRODUCT_ID.simpleAnnuity
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('getShoppingCart: ' + JSON.stringify(json));
  
  // iOS app says "No need to store in the keychain, we never retrieve it from the keychain."
  
  // Return the JSON
  return json;
};

export let verifyAgentCode = async (referralCode) => {
  
  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`verifyAgentCode error! current appData not found`);
  }
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`verifyAgentCode error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`verifyAgentCode error! pToken not found`);
  }
  
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHostAgent + NSRE_ENDPOINT.AgentInfo;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'userId': appData[APP_DATA.userId],
    'referralCode' : referralCode,
    'productId': PRODUCT_ID.simpleAnnuity
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('verifyAgentCode: ' + JSON.stringify(json));
  
  // Return the JSON
  return json;
};

export let verifyAgentCodeV01 = async (referralCode) => {
  
  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`verifyAgentCode error! current appData not found`);
  }
  
  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`verifyAgentCode error! principal not found`);
  }
  
  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`verifyAgentCode error! pToken not found`);
  }
  
  // TODO: Test for agent without producer code
  // USE? ?a=MDAwMDAw&ar=0 for test without producer code
  // USE: ?a=UkRJQTE=&ar=0 for test with producer code
  if (referralCode==='000000') {
    // form the agent info
    let agentInfoV01 ={};
    agentInfoV01[AGENT_INFO_V01.firstName] = 'Agent';
    agentInfoV01[AGENT_INFO_V01.lastName] = 'No-Producer-Code';
    agentInfoV01[AGENT_INFO_V01.email] = 'rhasankolli@nsre.com';
    agentInfoV01[AGENT_INFO_V01.mobile] = '203-555-1212';
    agentInfoV01[AGENT_INFO_V01.referralCode] = referralCode;
    agentInfoV01[AGENT_INFO_V01.firmName] = 'Test Firm';
    agentInfoV01[AGENT_INFO_V01.firmPhone] = '203-555-1212';
  
    // replace the current referral info in the json
    let json = {};
    json.referral = agentInfoV01;
    return json;
  }
  
  // TODO: update the URL to use the new agent API for D4a
  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.GetReferralAgent;
  
  // Form the POST body
  let body = JSON.stringify({
    'clientId': mygaSession.getClientId(),
    'userName': principal[PRINCIPAL.userName],
    'userId': appData[APP_DATA.userId],
    'referralCode' : referralCode,
    'productId': PRODUCT_ID.simpleAnnuity
  });
  
  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken
  };
  
  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });
  
  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('verifyAgentCode: ' + JSON.stringify(json));
  
  // TODO: Temporarily creating a AGENT_INFO_V01 object and mapping AGENT_INFO to it
  if (json.status === 'OK' && isValid(json.referral)) {
    
    // form the agent info
    let agentInfoV01 ={};
    
    // initialize recommendation unset
    agentInfoV01[AGENT_INFO.recommendationCode] = RECOMMENDATION_CODE.none;
    
    // Map the name
    if (isValid(json.referral[AGENT_INFO.name])) {
      let names = json.referral.name.split(/\s+/);
      agentInfoV01[AGENT_INFO_V01.firstName] = names.length>0? names[0] : '(none)';
      agentInfoV01[AGENT_INFO_V01.lastName] = names.length>1? names[1] : '(none)';
    }
    
    // map the email
    agentInfoV01[AGENT_INFO_V01.email] = json.referral[AGENT_INFO.email] || '(none)';
  
    // map the phone
    agentInfoV01[AGENT_INFO_V01.mobile] = json.referral[AGENT_INFO.phone] || '(none)';
  
    // map the producer code
    // 2021.06.28.ADivelbiss - New rule as of today.
    // If there is a producer code include it in the response
    // otherwise, include the firmName and firmPhone
    if (json.referral[AGENT_INFO.producerCodes].length>0) {
      agentInfoV01[AGENT_INFO_V01.producerCode] = json.referral[AGENT_INFO.producerCodes][0]['producerCode'];
    } else {
      agentInfoV01[AGENT_INFO_V01.firmName] = "Test Firm"
      agentInfoV01[AGENT_INFO_V01.firmPhone] = agentInfoV01[AGENT_INFO_V01.mobile] || "888-444-2222"
    }
  
    // map the referral code
    agentInfoV01[AGENT_INFO_V01.referralCode] = json.referral[AGENT_INFO.referralCode] || '(none)';
  
    // replace the current referral info in the json
    json.referral = agentInfoV01;
  }
  
  // Return the JSON
  return json;
};

export let idScan = async (base64ImageFront) => {

  // Get the current application
  let appData = mygaSession.getCurrentApp();
  if (!isValid(appData)) {
    throw new Error(`verifyAgentCode error! current appData not found`);
  }

  // Get the principal if it exists.
  let principal = await mygaSession.getPrincipal();
  if (!isValid(principal)) {
    throw new Error(`startApplication error! principal not found`);
  }

  // Get the pToken
  let pToken = principal[PRINCIPAL.pToken];
  if (!isValid(pToken)) {
    throw new Error(`startApplication error! pToken not found`);
  }

  // Form the Url
  let url = CONFIG.nsreAPI.nsreHost + NSRE_ENDPOINT.IDScan;

  //debugger;

  // Form the POST body
  let body = JSON.stringify({
    //'clientId': mygaSession.getClientId(),
    //'userName': principal[PRINCIPAL.userName],
    'userId': appData[APP_DATA.userId],
    'productId': PRODUCT_ID.simpleAnnuity,
    'base64ImageFront': base64ImageFront,
    'base64ImageBack': ''
  });

  // Form the post headers
  let headers = {
    'Content-Type':'application/json',
    'Authorization': pToken,
    'X-API-Key': CONFIG.nsreAPI.idScanAPIKey
  };

  // Make the call
  let response = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: body
  });

  // convert the result to json
  let json = await jsonResponseHandler(response);
  NassauLog('idscan: ' + JSON.stringify(json));

  // Return the result
  return JSON.parse(json.body);
};

export const saveValidationReport = async () => {
  // only save if we have not already submitted the current application.
  if (mygaSession.getCurrentAppSubmitStatus()!=='OK') {
    const validation = getValidationReport();
    // NassauLog(`validation: ${JSON.stringify(validation,null,2)}`);
    if (isValid(validation)) {
      mygaSession.setCurrentAppSectionData(APP_SECTION.validation,validation);
      return saveApplication(APP_SECTION.validation);
    }
  }
}